@if (visible$ | async) {
  <div class="print-panel">
    @if ((busy$ | async) === false) {
      @if ((hasVisibleLayers$ | async)) {
        <h2 class="section-label" i18n="@@core.print.export-map">Export map</h2>
        <div class="form-field">
          <mat-button-toggle-group [formControl]="exportType">
            <mat-button-toggle value="pdf" i18n="@@core.print.pdf-document">PDF document</mat-button-toggle>
            <mat-button-toggle value="image" i18n="@@core.print.map-image">Map image</mat-button-toggle>
          </mat-button-toggle-group>
        </div>
        @if (hasDrawing$ | async) {
          <div class="spaced spaced_top">
            <mat-checkbox color="primary" [formControl]="includeDrawing" i18n="@@core.print.include-drawing">Include drawing</mat-checkbox>
          </div>
        }
        @if (exportType.value === 'image') {
          <div class="spaced spaced_top">
            <form [formGroup]="exportImageForm" (ngSubmit)="downloadMapImage()">
              <ng-container i18n="@@core.print.image-size-mm">Image size in millimeters:</ng-container>
              <p>
                <mat-form-field appearance="outline" color="primary" hideRequiredMarker>
                  <mat-label><span i18n="@@core.print.width">Width</span>&nbsp;<em i18n="@@core.common.required">(required)</em></mat-label>
                  <mat-hint align="end" i18n="@@core.print.width-range">Range: 50 - 600</mat-hint>
                  <input matInput formControlName="width" type="number" min="50" max="600">
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline" color="primary" hideRequiredMarker>
                  <mat-label><span i18n="@@core.print.height">Height</span>&nbsp;<em i18n="@@core.common.required">(required)</em></mat-label>
                  <mat-hint align="end" i18n="@@core.print.height-range">Range: 50 - 600</mat-hint>
                  <input matInput formControlName="height" type="number" min="50" max="600">
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline" color="primary" hideRequiredMarker>
                  <mat-label><span i18n="@@core.print.dpi">DPI</span>&nbsp;<em i18n="@@core.common.required">(required)</em></mat-label>
                  <mat-hint align="end" i18n="@@core.print.dpi-range">Range: 90 - 600</mat-hint>
                  <input matInput formControlName="dpi" type="number" min="90" max="600">
                </mat-form-field>
              </p>
              <p>
                <div [style.visibility]="exportImageForm.valid ? 'visible' : 'hidden'">
                  <div class="padding" i18n="@@core.print.image-size-in-px">Image size in pixels: {{getImageResolution()}}</div>
                  <div class="padding" i18n="@@core.print.width-height-ratio">Width/height ratio: {{getImageRatio() | number: '1.1-2' }}</div>
                </div>
                <div class="padding">
                  <button mat-flat-button color="primary" type="submit" [disabled]="!exportImageForm.valid" i18n="@@core.print.download-map-image">Download map image</button>
                </div>
              </form>
            </div>
          }
          @if (exportType.value === 'pdf') {
            <div class="spaced">
              <form [formGroup]="exportPdfForm" (ngSubmit)="downloadPdf()">
                <p class="form-field">
                  <mat-button-toggle-group formControlName="orientation" aria-label="Orientation" i18n-aria-label="@@core.print.orientation">
                    <mat-button-toggle value="landscape" i18n="@@core.print.landscape">Landscape</mat-button-toggle>
                    <mat-button-toggle value="portrait" i18n="@@core.print.portrait">Portrait</mat-button-toggle>
                  </mat-button-toggle-group>
                </p>
                <p class="form-field">
                  <mat-button-toggle-group formControlName="paperSize" style="margin: 3px" aria-label="Paper size" i18n-aria-label="@@core.print.paper-size">
                    <mat-button-toggle value="a4" i18n="@@core.print.a4">A4</mat-button-toggle>
                    <mat-button-toggle value="a3" i18n="@@core.print.a3">A3</mat-button-toggle>
                  </mat-button-toggle-group>
                </p>
                <p>
                  <mat-form-field appearance="outline" color="primary" class="spaced_top">
                    <mat-label i18n="@@core.print.title">Title</mat-label>
                    <input #title matInput formControlName="title" maxlength="60">
                    <mat-hint align="end">{{title.value.length || 0}}/60</mat-hint>
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline" color="primary">
                    <mat-label i18n="@@core.print.footer">Footer</mat-label>
                    <input #footer matInput formControlName="footer" maxlength="60">
                    <mat-hint align="end">{{footer.value.length || 0}}/60</mat-hint>
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline" color="primary" hideRequiredMarker>
                    <mat-label><span i18n="@@core.print.dpi">DPI</span>&nbsp;<em i18n="@@core.common.required">(required)</em></mat-label>
                    <mat-hint align="end" i18n="@@core.print.dpi-range">Range: 90 - 600</mat-hint>
                    <input matInput formControlName="dpi" type="number" min="90" max="600">
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline" color="primary">
                    <mat-label i18n="@@core.print.add-legend-for-layer">Add legend for layer</mat-label>
                    <mat-select formControlName="legendLayer">
                      <mat-option i18n="@@core.common.none">None</mat-option>
                      @for (layer of availableLegendLayers$ | async; track layer) {
                        <mat-option  [value]="layer.id">{{layer.title}}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </p>
                <p>
                  <mat-checkbox color="primary" formControlName="showBookmark" i18n="@@core.print.add-bookmark">Add bookmark to document</mat-checkbox>
                </p>
                <p>
                  <mat-checkbox color="primary" formControlName="autoPrint" i18n="@@core.print.open-print-dialog">Automatically open print dialog</mat-checkbox>
                </p>
                <div class="padding">
                  <button mat-flat-button color="primary" type="submit" [disabled]="!exportPdfForm.valid" i18n="@@core.print.download-map-pdf">Download map PDF</button>
                </div>
              </form>
            </div>
          }
        } @else {
          <ng-container i18n="@@core.print.no-visible-layers">No visible layers.</ng-container>
        }
      } @else {
        <p i18n="@@core.print.creating-map">Creating map, this may take a while.</p>
        <div class="centered spaced">
          <mat-spinner style="margin: 0 auto" color="primary" mode="indeterminate" diameter="20"></mat-spinner>
        </div>
        <button mat-flat-button color="primary" (click)="cancel()" i18n="@@core.common.cancel">Cancel</button>
      }
    </div>
  }
